import { useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Download, MessageSquare, Upload, ArrowLeft, CheckCircle, Gamepad2, ChevronDown, Clock, User, Eye, EyeOff } from "lucide-react";
import GameLinksBox from "@/components/GameLinksBox";
import GameStatsBox from "@/components/GameStatsBox";
import GameTabs from "@/components/GameTabs";
import GameAdminPanel from "@/components/GameAdminPanel";
import EditGameForm from "@/components/EditGameForm";
import UploadTranslationForm from "@/components/UploadTranslationForm";
import { useState, useEffect, useCallback } from "react";
import { showSuccess, showError } from "@/utils/toast";
import SuggestVideoModal from "@/components/SuggestVideoModal";
import { useAuth } from "@/context/AuthContext";
import GameYoutubeBox from '@/components/GameYoutubeBox';
import GameTwitchBox from '@/components/GameTwitchBox'; // Új import
import GameAdminTagsPanel from "@/components/GameAdminTagsPanel"; // Import the new component
import GameReportAndRequestBox from "@/components/GameReportAndRequestBox"; // Import the new component
import GameSimilarGamesBox from "@/components/GameSimilarGamesBox"; // Import the similar games component
import GameInfoPanel from "@/components/GameInfoPanel"; // Import GameInfoPanel


interface Genre {
  id: number;
  name: string;
}

interface Tag {
  id: string;
  name_hu: string;
  name_en: string;
}

interface YoutubeVideo {
  id?: string;
  url: string;
  title: string;
  userId: number;
  channelName: string;
  channelIcon: string;
  hasReported: boolean;
  status?: string;
}

// Új interface a Twitch videókhoz
interface TwitchVideo {
  id?: string;
  url: string;
  title: string;
  userId: number;
  channelName: string;
  channelIcon: string;
}

const getEditionName = (editionId: number | string): string => {
  switch (String(editionId)) {
    case '0':
      return 'Digital Deluxe';
    case '1':
      return 'Standard';
    case '8':
      return 'Deluxe';
    default:
      return String(editionId); // Return the ID itself if no mapping is found
  }
};

interface GameData {
  id: number;
  name: string;
  ads: boolean;
  description: string;
  image: string;
  genres: Genre[];
  tags: Tag[];
  likes: number;
  downloads: number;
  comments: number;
  developer?: string;
  publisher?: string;
  releaseDate: string;
  link_steam?: string;
  link_epic?: string;
  link_other?: string;
  link_hun?: string;
  developerWebsiteLink?: string;
  uploads?: number;
  facebookLink?: string;
  pegiRating?: string;
  isAgeRestricted?: boolean;
  slug?: string;
  canComment?: boolean;
  hasOfficialTranslation?: boolean;
  hungarianRelease?: boolean;
  commentReason?: string;
  hasAds?: boolean;
  platforms?: string[];
  allow_comments?: boolean;
  reason?: string;
  youtube_videos?: YoutubeVideo[];
  twitch_videos?: TwitchVideo[]; // Új property
  game_infos?: any[]; // GameInfo+ jegyzetek
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  hidden?: boolean;
  xxx?: boolean;
  view_count?: number;
  uploads_count?: number;
  downloads_count?: number;
  thanks_count?: number;
  messages_count?: number;
}

const GameShow = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isTranslator, isAdmin, isLoggedIn, currentUser, showShoppingOffers, isRealModerator } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams(); // <-- Hook inicializálása
  const location = useLocation();
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [loadingGame, setLoadingGame] = useState(true);
  const [errorGame, setErrorGame] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showHardlockDialog, setShowHardlockDialog] = useState(false);
  const [gameHardlock, setGameHardlock] = useState<any>(null);
  const [hardlockLoading, setHardlockLoading] = useState(false);
  const [showMagyaritasWarningAcknowledged, setShowMagyaritasWarningAcknowledged] = useState(false);
  // State-ek a célzott navigációhoz (hozzáadva a GameShow2 alapján)
  const [activeTab, setActiveTab] = useState('downloads');
  const [targetCommentPage, setTargetCommentPage] = useState<number | null>(null);
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null);
  const [isSuggestVideoModalOpen, setIsSuggestVideoModalOpen] = useState(false);
  const [isTagsPanelOpen, setIsTagsPanelOpen] = useState(false); // State for the tags panel
  const [instantGameData, setInstantGameData] = useState<any[] | null>(null); // New state for instant game offers, now an array
  // DISABLED: gg.deals states - replaced with IsThereAnyDeal
  // const [ggDealsData, setGgDealsData] = useState<any | null>(null); // New state for gg.deals offers
  // const [loadingGgDeals, setLoadingGgDeals] = useState(false);
  const [isthereanydealData, setIsthereanydealData] = useState<any | null>(null); // New state for IsThereAnyDeal offers
  const [loadingIsthereanydeal, setLoadingIsthereanydeal] = useState(false);

  // useEffect a kommentre navigáláshoz (hozzáadva a GameShow2 alapján)
  useEffect(() => {
    const commentId = searchParams.get('commentId');
    if (commentId) {
      const navigateToComment = async () => {
        try {
          // Feltételezzük, hogy 5 komment van egy oldalon
          const perPage = 5;
          const response = await fetch(`/api/comments/location?commentId=${encodeURIComponent(commentId)}&perPage=${perPage}`);
          if (!response.ok) throw new Error("Hiba történt a komment helyének lekérésekor.");
          const location = await response.json();
          if (location) {
            setActiveTab('comments');
            setTargetCommentPage(location.page);
            setTargetCommentId(commentId);
            // URL tisztítása a navigációs logika lefutása után
            searchParams.delete('commentId');
            setSearchParams(searchParams, { replace: true });
          }
        } catch (error) {
          console.error("Komment helyének lekérése sikertelen:", error);
        }
      };
      navigateToComment();
    }
  }, [searchParams, setSearchParams]);

  const loadGame = useCallback(async () => {
    setLoadingGame(true);
    setErrorGame(null);
    try {
      const response = await fetch(`/api/games/${encodeURIComponent(slug || '')}`);
      if (!response.ok) {
        setErrorGame("A játék nem található.");
        return;
      }
      const data = await response.json();
      if (!data || !data.id) {
        setErrorGame("A játékadatok hibásak vagy hiányosak.");
      } else {
        setGameData(data);
      }
    } catch (error) {
      setErrorGame("Játék betöltése sikertelen (szerver- vagy hálózati hiba).");
    } finally {
      setLoadingGame(false);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadGame();
  }, [loadGame]);

  // useEffect a document title, meta description és social tagek beállításához
  useEffect(() => {
    if (gameData?.name) {
      const title = `${gameData.name} Magyarítás Letöltés (PC) – Magyar Fordítás | GEP.MONSTER`;
      document.title = title;

      // Meta description beállítása
      const metaDescription = `${gameData.name} magyarítás letöltés PC-re. Magyar fordítás, telepítési információk és kompatibilis verziók egy helyen a GEP.MONSTER oldalán.`;
      let metaTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'description');
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', metaDescription);

      // Helper to set meta[property] tags (OG)
      const setMetaProperty = (prop: string, content: string) => {
        let tag = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', prop);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      setMetaProperty('og:title', title);
      setMetaProperty('og:description', metaDescription);
      setMetaProperty('og:type', 'website');
      setMetaProperty('og:url', window.location.href);
      if (gameData.image) setMetaProperty('og:image', gameData.image);

      // Twitter card
      let twitterCard = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement | null;
      if (!twitterCard) {
        twitterCard = document.createElement('meta');
        twitterCard.setAttribute('name', 'twitter:card');
        document.head.appendChild(twitterCard);
      }
      twitterCard.setAttribute('content', gameData.image ? 'summary_large_image' : 'summary');

      let twitterTitle = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement | null;
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', title);

      let twitterDesc = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement | null;
      if (!twitterDesc) {
        twitterDesc = document.createElement('meta');
        twitterDesc.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDesc);
      }
      twitterDesc.setAttribute('content', metaDescription);

      // canonical link
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', window.location.href);
    }
  }, [gameData?.name, gameData?.image]);

  // New useEffect to fetch instant game data
  useEffect(() => {
    if (gameData?.link_steam) {
      const fetchInstantGame = async () => {
        try {
          // Extract steam_id from link_steam
          const steamIdMatch = gameData.link_steam?.match(/\/app\/(\d+)\//);
          const steamId = steamIdMatch ? steamIdMatch[1] : null;

          if (steamId) {
            const response = await fetch(`/api/instantgames/${steamId}`); // Hypothetical API endpoint
            if (response.ok) {
              const data = await response.json();
              setInstantGameData(data); // Expecting an array now
            } else {
              console.error("Failed to fetch instant game data");
              setInstantGameData(null); // Clear data on error
            }
          }
        } catch (error) {
          console.error("Error fetching instant game:", error);
          setInstantGameData(null); // Clear data on error
        }
      };
      fetchInstantGame();
    }
  }, [gameData?.link_steam]); // Re-run when gameData.link_steam changes

  // DISABLED: gg.deals fetching - replaced with IsThereAnyDeal
  useEffect(() => {
    // GG.Deals fetching disabled - using IsThereAnyDeal instead
    return;

    // The code below is disabled and kept for reference only:
    /*
    if (gameData?.link_steam) {
      const fetchGgDeals = async () => {
        try {
          // Extract steam_id from link_steam
          const steamIdMatch = gameData.link_steam?.match(/\/app\/(\d+)\//);
          const steamId = steamIdMatch ? steamIdMatch[1] : null;

          if (steamId) {
            // Check if there are stocked instant game offers first
            const hasStockedOffers = instantGameData?.some(offer => {
              const isStocked = offer.stock && (
                String(offer.stock).toLowerCase().includes('in stock') ||
                String(offer.stock).toLowerCase().includes('available') ||
                String(offer.stock).toLowerCase().includes('yes') ||
                String(offer.stock) === '1'
              );
              return isStocked;
            });

            // Only fetch gg.deals if there are no stocked instant game offers
            if (!hasStockedOffers) {
              setLoadingGgDeals(true);
              const response = await fetch(`/api/recommendations/ggdeals?steam_id=${steamId}`);
              if (response.ok) {
                const data = await response.json();
                setGgDealsData(data);
              } else {
                setGgDealsData(null);
              }
            } else {
              setGgDealsData(null); // Clear gg.deals data if instant game offers exist
            }
          }
        } catch (error) {
          console.error("Error fetching gg.deals data:", error);
          setGgDealsData(null);
        } finally {
          setLoadingGgDeals(false);
        }
      };
      fetchGgDeals();
    }
    */
  }, [gameData?.link_steam, instantGameData]);

  // New useEffect to fetch IsThereAnyDeal data if no instant game offers
  useEffect(() => {
    if (gameData?.link_steam) {
      const fetchIsThereAnyDeal = async () => {
        try {
          // Extract steam_id from link_steam
          const steamIdMatch = gameData.link_steam?.match(/\/app\/(\d+)\//);
          const steamId = steamIdMatch ? steamIdMatch[1] : null;

          if (steamId || gameData?.name) {
            // Check if there are stocked instant game offers first
            const hasStockedOffers = instantGameData?.some(offer => {
              const isStocked = offer.stock && (
                String(offer.stock).toLowerCase().includes('in stock') ||
                String(offer.stock).toLowerCase().includes('available') ||
                String(offer.stock).toLowerCase().includes('yes') ||
                String(offer.stock) === '1'
              );
              return isStocked;
            });

            // Only fetch IsThereAnyDeal if there are no stocked instant game offers
            if (!hasStockedOffers) {
              setLoadingIsthereanydeal(true);
              const params = new URLSearchParams();
              if (steamId) params.append('steam_id', steamId);
              if (gameData?.name) params.append('game_name', gameData.name);
              const response = await fetch(`/api/isthereanydeal/offers?${params.toString()}`);
              if (response.ok) {
                const data = await response.json();
                setIsthereanydealData(data);
              } else {
                setIsthereanydealData(null);
              }
            } else {
              setIsthereanydealData(null); // Clear IsThereAnyDeal data if instant game offers exist
            }
          }
        } catch (error) {
          console.error("Error fetching IsThereAnyDeal data:", error);
          setIsthereanydealData(null);
        } finally {
          setLoadingIsthereanydeal(false);
        }
      };
      fetchIsThereAnyDeal();
    }
  }, [gameData?.link_steam, instantGameData]);



  const handleSaveGame = (updatedGame: any) => {
    setGameData(prev => prev ? { ...prev, ...updatedGame } : null);
    setShowEditForm(false);
    showSuccess("Játék sikeresen frissítve!");
  };

  const handleUploadSuccess = () => {
    loadGame();
    setShowUploadForm(false);
  };

  // Hardlock betöltése
  useEffect(() => {
    if (!gameData?.id) return;

    const fetchHardlock = async () => {
      try {
        setHardlockLoading(true);
        const response = await fetch(`/api/games/${gameData.id}/hardlock`);
        if (response.ok) {
          const data = await response.json();
          setGameHardlock(data.hardlock);
        }
      } catch (error) {
        console.error('Error fetching hardlock:', error);
      } finally {
        setHardlockLoading(false);
      }
    };

    fetchHardlock();
  }, [gameData?.id]);

  // Upload gomb kezelése - hardlock ellenőrzéssel
  const handleUploadClick = () => {
    // Debug info
    console.log("handleUploadClick - gameHardlock:", gameHardlock);
    console.log("handleUploadClick - currentUser.translatorId:", currentUser?.translatorId);
    console.log("handleUploadClick - isTranslator:", isTranslator);

    // Ha van aktív hardlock és nem az ő fordítója, feloldási kérelem dialógust nyitunk meg
    if (gameHardlock && gameHardlock.status === 'active') {
      console.log("Active hardlock found. Owner ID:", gameHardlock.translater_id, "Current user:", currentUser?.translatorId);

      if (gameHardlock.translater_id !== currentUser?.translatorId) {
        console.log("User is NOT the hardlock owner - showing hardlock dialog");
        setShowHardlockDialog(true);
        return;
      } else {
        console.log("User IS the hardlock owner - allowing upload form");
      }
    }

    // Nincs hardlock vagy az ő fordítója - upload forma megnyitása
    console.log("Opening/closing upload form");
    setShowUploadForm(p => !p);
  };

  if (loadingGame) return <LoadingState />;
  if (errorGame || !gameData) return <ErrorState message={errorGame} isAdmin={isAdmin} />;

  const allVideos = gameData.youtube_videos || [];
  const videosToShow = allVideos;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="sticky top-0 z-50 w-full">
        <Header />
      </div>
      <main className="flex-grow container mx-auto py-8 main-content-bg mt-[12px]">
        <Link
          to={(location.state as { from?: string } | null)?.from ?? (gameData?.xxx ? "/xxx" : "/")}
          className="flex items-center text-primary hover:text-primary/40 transition-colors mb-6"
        >
          <ArrowLeft size={20} className="mr-2" /> Vissza a játékokhoz
        </Link>

        <GameTitleSection game={gameData} isAdmin={isAdmin} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <img src={gameData.image} alt={gameData.name} className="w-full h-auto object-cover rounded-xl shadow-lg" decoding="async" crossOrigin="anonymous" loading="eager" />


            {/* Hardlock Info Panel - Akkor jelenik meg, ha van aktív hardlock és a felhasználó jogosult a megtekintésre */}
            {gameHardlock && gameHardlock.status === 'active' && (
              currentUser?.translatorId === gameHardlock.translater_id ||
              isAdmin ||
              isRealModerator
            ) && (
                <Card className="bg-yellow-500/10 border border-yellow-500/30 shadow-lg">
                  <CardHeader><CardTitle className="text-xl font-bold text-yellow-600">🔒 Fordítási Jog Védelem</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm space-y-2">
                      <p><strong>Fordító:</strong> {gameHardlock.translater_name}</p>
                      <p><strong>Védelem kezdete:</strong> {new Date(gameHardlock.locked_at).toLocaleString('hu-HU')}</p>
                      {gameHardlock.unlock_requested_at && (
                        <>
                          <p><strong>Feloldás kérelem:</strong> {new Date(gameHardlock.unlock_requested_at).toLocaleString('hu-HU')}</p>
                          <p><strong>Maradék idő:</strong> {gameHardlock.days_remaining} nap</p>
                        </>
                      )}
                      {gameHardlock.is_forever_locked && (
                        <p className="text-yellow-600 font-semibold">⏳ Örökre védve, amíg feloldást nem kérnek</p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 pt-3">
                      {/* Feloldás kérelem - Bárki kérheti */}
                      {!gameHardlock.unlock_requested_at && (
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/games/${gameData.id}/hardlock/request_unlock`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ reason: 'Feloldás kérelem' })
                              });
                              if (response.ok) {
                                showSuccess('✅ Feloldás kérelem elküldve! (3 nap védelem)');
                                // Újratöltjük a hardlock adatot
                                window.location.reload();
                              } else {
                                showError('❌ Hiba a feloldás kérelem küldésekor');
                              }
                            } catch (error) {
                              showError('❌ Hiba a feloldás kérelem küldésekor');
                            }
                          }}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md text-sm font-medium transition"
                        >
                          🔓 Feloldás Kérelem
                        </button>
                      )}

                      {/* Azonnali Feloldás - Csak owner/admin/realModerator */}
                      {(
                        currentUser?.translatorId === gameHardlock.translater_id ||
                        isAdmin ||
                        isRealModerator
                      ) && (
                          <button
                            onClick={async () => {
                              if (!confirm('Biztosan feloldod a fordítási jog védelmet?')) return;

                              try {
                                const response = await fetch(`/api/games/${gameData.id}/hardlock/admin_release`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ reason: 'Azonnali feloldás' })
                                });
                                if (response.ok) {
                                  showSuccess('✅ Fordítási jog védelem feloldva!');
                                  window.location.reload();
                                } else {
                                  showError('❌ Hiba a feloldás során');
                                }
                              } catch (error) {
                                showError('❌ Hiba a feloldás során');
                              }
                            }}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition"
                          >
                            ⚡ Azonnali Feloldás
                          </button>
                        )}
                    </div>
                  </CardContent>
                </Card>
              )}

            {(isTranslator || isAdmin || isRealModerator) && <GameAdminPanel gameId={gameData.id} initialHasOfficialTranslation={gameData.hasOfficialTranslation ?? false} initialHungarianRelease={gameData.hungarianRelease ?? false} initialHidden={gameData.hidden ?? false} onEditClick={() => setShowEditForm(p => !p)} onUploadClick={handleUploadClick} link_hun={gameData.link_hun} />}

            {showEditForm && (
              <Card className="bg-card text-card-foreground border-border shadow-lg">
                <CardHeader><CardTitle className="text-2xl font-bold text-primary">Játék szerkesztése</CardTitle></CardHeader>
                <CardContent><EditGameForm game={gameData} onSave={handleSaveGame} onCancel={() => setShowEditForm(false)} /></CardContent>
              </Card>
            )}

            {showUploadForm && (
              <Card className="bg-card text-card-foreground border-border shadow-lg">
                <CardHeader><CardTitle className="text-2xl font-bold text-primary">Fordítás feltöltése</CardTitle></CardHeader>
                <CardContent><UploadTranslationForm gameId={gameData.id} onUploadSuccess={handleUploadSuccess} onCancel={() => setShowUploadForm(false)} isAdmin={isAdmin} gameHardlock={gameHardlock} hardlockLoading={hardlockLoading} /></CardContent>
              </Card>
            )}

            {showHardlockDialog && gameHardlock && (
              <Card className="bg-red-500/10 border border-red-500/20 shadow-lg">
                <CardHeader><CardTitle className="text-2xl font-bold text-red-400">🔒 Ez a játék hardlock alatt áll</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-3">
                    <p><strong>{gameHardlock.translater_name}</strong> fordítási joga védelem alatt van.</p>
                    {gameHardlock.unlock_requested_at ? (
                      <>
                        <p className="font-semibold">⏳ Feloldás kérelem már indítva:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li><strong>Kérelmező:</strong> {gameHardlock.unlock_requested_by_name}</li>
                          <li><strong>Dátum:</strong> {new Date(gameHardlock.unlock_requested_at).toLocaleString('hu-HU')}</li>
                          <li><strong>Maradék idő:</strong> {gameHardlock.days_remaining} nap</li>
                          {gameHardlock.unlock_request_reason && <li><strong>Indoklás:</strong> {gameHardlock.unlock_request_reason}</li>}
                        </ul>
                      </>
                    ) : (
                      <p>Feloldást kérhetsz a hardlock-hoz, amely után 3 nap védelmi periódus lesz.</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isTranslator && !gameHardlock.unlock_requested_at && (
                      <Button
                        onClick={async () => {
                          try {
                            const response = await fetch(`/api/games/${gameData.id}/hardlock/request_unlock`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ reason: '' })
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setGameHardlock(data.hardlock);
                              showSuccess('✅ Feloldás kérelem elküldve!');
                            } else {
                              showError('❌ Hiba a feloldás kérelem küldésekor');
                            }
                          } catch (err) {
                            showError('❌ Hiba a feloldás kérelem küldésekor');
                          }
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        🔓 Feloldás kérése
                      </Button>
                    )}
                    <Button onClick={() => setShowHardlockDialog(false)} className="flex-1">
                      Bezárás
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* GameInfo+ Panel - Fordítók gondolatai (a leírás felett) */}
            {((isAdmin && isLoggedIn) || (gameData.game_infos && gameData.game_infos.length > 0)) && (
              <GameInfoPanel gameId={gameData.id} initialInfos={gameData.game_infos || []} />
            )}

            {gameData.description && (
              <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md mb-6">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary">
                    Leírás
                    <span className="ml-2 inline-block px-2 py-1 bg-blue-900/60 text-blue-300 text-xs rounded border border-blue-700">
                      🤖 Gemini fordítás
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-lg text-muted-foreground"
                    dangerouslySetInnerHTML={{
                      __html: gameData.description
                        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 font-bold">&lt;LINK&gt;</a>'),
                    }}
                  />
                </CardContent>
              </Card>
            )}

            <GameSimilarGamesBox gameId={gameData.id} gameSlug={gameData.slug || ''} />

            {(() => {
              // Ajánlatok megjelenítése az AuthContext érték alapján
              // Alapértelmezésben TRUE - csak akkor rejtve, ha explicit FALSE-ra állította
              const showOffers = showShoppingOffers;

              const stockedOffers = instantGameData?.filter(offer => {
                const isStocked = offer.stock && (
                  String(offer.stock).toLowerCase().includes('in stock') ||
                  String(offer.stock).toLowerCase().includes('available') ||
                  String(offer.stock).toLowerCase().includes('yes') ||
                  String(offer.stock) === '1'
                );
                return isStocked;
              }) || [];

              if (stockedOffers.length === 0 || !showOffers) {
                return null;
              }

              return (
                <Card className="bg-card text-card-foreground border-border shadow-lg mb-6">
                  <CardHeader><CardTitle className="text-xl font-bold text-primary">Instant Gaming ajánlatok</CardTitle></CardHeader>
                  <CardContent>
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-6 rounded text-center">
                      <p className="text-yellow-800 font-semibold">Ha nem vetted még meg a játékot, de szeretnéd, itt eredetiben megveheted, jelentős kedvezményekkel!</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-secondary text-foreground">
                            <th className="px-4 py-2 text-left">Ár</th>
                            <th className="px-4 py-2 text-center">Platform</th>
                            <th className="px-4 py-2 text-center">Kategória</th>
                            <th className="px-4 py-2 text-center">Edition</th>
                            <th className="px-4 py-2 text-center">Régió</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockedOffers.map((offer: any, index: number) => (
                            <tr
                              key={offer.id || index}
                              className="bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer border-b border-border last:border-b-0"
                              onClick={() => window.open(`${offer.url.split('?')[0]}?igr=gamer-32cbcdb`, '_blank')}
                            >
                              <td className="px-4 py-2 text-left text-sm font-semibold">
                                {offer.retail && offer.retail !== offer.price && (
                                  <s className="text-muted-foreground mr-1">{offer.retail}€</s>
                                )}
                                <span>{offer.price}€</span>
                              </td>
                              <td className="px-4 py-2 text-center text-xs opacity-80">
                                {offer.systems ? offer.systems.substring(0, 3).toUpperCase() : '-'}
                              </td>
                              <td className="px-4 py-2 text-center text-sm">
                                {offer.category && offer.category.toUpperCase() === 'STEAM' ? (
                                  <i className="bi bi-steam" style={{ fontSize: '16px' }}></i>
                                ) : offer.category && (offer.category.toUpperCase() === 'MICROSOFT STORE' || offer.category.toUpperCase() === 'MICROSOFT') ? (
                                  <i className="bi bi-xbox" style={{ fontSize: '16px' }}></i>
                                ) : offer.category && offer.category.toUpperCase() === 'PLAYSTATION STORE' ? (
                                  <i className="bi bi-playstation" style={{ fontSize: '16px' }}></i>
                                ) : (
                                  offer.category ? offer.category : '-'
                                )}
                              </td>
                              <td className="px-4 py-2 text-center text-sm">
                                {offer.edition_id ? getEditionName(offer.edition_id) : '-'}
                              </td>
                              <td className="px-4 py-2 text-center text-sm">
                                {offer.region ? offer.region.toUpperCase() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

              );
            })()}

            {/* GG.Deals Panel - LETILTVA - megjelenik ha nincs InstantGaming ajánlat */}
            {(() => {
              // LETILTVA EGYELŐRE
              return null;

              // Az alábbi kód marad, de nem fut le:
              /*
              const showOffers = showShoppingOffers;
              const stockedOffers = instantGameData?.filter(offer => {
                const isStocked = offer.stock && (
                  String(offer.stock).toLowerCase().includes('in stock') ||
                  String(offer.stock).toLowerCase().includes('available') ||
                  String(offer.stock).toLowerCase().includes('yes') ||
                  String(offer.stock) === '1'
                );
                return isStocked;
              }) || [];

              // Csak megjelenítse a GG.Deals panelt, ha nincs InstantGaming ajánlat
              if (stockedOffers.length > 0 || !showOffers || !ggDealsData?.data) {
                return null;
              }

              // Az gg.deals API válasz feldolgozása
              const gameData = Object.values(ggDealsData.data)[0] as any;
              if (!gameData) return null;

              const prices = gameData.prices;
              if (!prices) return null;

              return (
                <Card className="bg-card text-card-foreground border-border shadow-lg mb-6">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary">GG.Deals Ajánlatok</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-blue-100 border-l-4 border-blue-500 p-4 mb-6 rounded text-center">
                      <p className="text-blue-800 font-semibold">Gyors ajánlatok a GG.Deals-ről - összehasonlítja az online áruházakban rendelkezésre álló árakat!</p>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-secondary p-4 rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground mb-2">Aktuális Kulcskereskedő Ár</p>
                          <p className="text-2xl font-bold text-primary">
                            {prices.currentKeyshops ? `${prices.currentKeyshops} ${prices.currency}` : 'N/A'}
                          </p>
                        </div>
                        <div className="bg-secondary p-4 rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground mb-2">Aktuális Retail Ár</p>
                          <p className="text-2xl font-bold text-primary">
                            {prices.currentRetail ? `${prices.currentRetail} ${prices.currency}` : 'N/A'}
                          </p>
                        </div>
                        <div className="bg-secondary p-4 rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground mb-2">Történelmi Legalacsonyabb (Kulcskereskedő)</p>
                          <p className="text-lg font-semibold text-green-400">
                            {prices.historicalKeyshops ? `${prices.historicalKeyshops} ${prices.currency}` : 'N/A'}
                          </p>
                        </div>
                        <div className="bg-secondary p-4 rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground mb-2">Történelmi Legalacsonyabb (Retail)</p>
                          <p className="text-lg font-semibold text-green-400">
                            {prices.historicalRetail ? `${prices.historicalRetail} ${prices.currency}` : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => window.open(gameData.url, '_blank')}
                        className="w-full mt-4 bg-primary hover:bg-primary/80"
                      >
                        Megnyitás a GG.Deals-en
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
              */
            })()}

            {/* IsThereAnyDeal Panel - megjelenik ha nincs InstantGaming ajánlat */}
            {(() => {
              const showOffers = showShoppingOffers;
              const stockedOffers = instantGameData?.filter(offer => {
                const isStocked = offer.stock && (
                  String(offer.stock).toLowerCase().includes('in stock') ||
                  String(offer.stock).toLowerCase().includes('available') ||
                  String(offer.stock).toLowerCase().includes('yes') ||
                  String(offer.stock) === '1'
                );
                return isStocked;
              }) || [];

              // Csak megjelenítse az IsThereAnyDeal panelt, ha nincs InstantGaming ajánlat
              if (stockedOffers.length > 0 || !showOffers || !isthereanydealData?.offers) {
                return null;
              }

              const offers = isthereanydealData.offers || [];
              if (offers.length === 0) return null;

              // Szűrjük a leginább kedvezményes ajánlatokat és rendezzük kedvezmény alapján
              const sortedOffers = offers.sort((a, b) => (b.cut || 0) - (a.cut || 0));
              const bestOffers = sortedOffers.slice(0, 10);

              return (
                <Card className="bg-card text-card-foreground border-border shadow-lg mb-6">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary">IsThereAnyDeal Ajánlatok</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-green-100 border-l-4 border-green-500 p-4 mb-6 rounded text-center">
                      <p className="text-green-800 font-semibold">Keresés az interneten az összes áruházban - IsThereAnyDeal segít megtalálni a legjobb árakat!</p>
                    </div>

                    {/* Árhistória infó szakasz */}
                    {isthereanydealData?.history_low && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
                        <div className="bg-secondary p-3 sm:p-4 rounded-lg border border-border">
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 line-clamp-2">Történelmi Legalacsonyabb (Összes)</p>
                          <p className="text-base sm:text-lg font-bold text-green-400">
                            {isthereanydealData.history_low.all?.amount?.toFixed(2)} {isthereanydealData.history_low.all?.currency}
                          </p>
                        </div>
                        <div className="bg-secondary p-3 sm:p-4 rounded-lg border border-border">
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 line-clamp-2">Legalacsonyabb (1 év)</p>
                          <p className="text-base sm:text-lg font-bold text-blue-400">
                            {isthereanydealData.history_low.y1?.amount?.toFixed(2)} {isthereanydealData.history_low.y1?.currency}
                          </p>
                        </div>
                        <div className="bg-secondary p-3 sm:p-4 rounded-lg border border-border">
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 line-clamp-2">Legalacsonyabb (3 hónap)</p>
                          <p className="text-base sm:text-lg font-bold text-purple-400">
                            {isthereanydealData.history_low.m3?.amount?.toFixed(2)} {isthereanydealData.history_low.m3?.currency}
                          </p>
                        </div>
                      </div>
                    )}

                    {loadingIsthereanydeal && <p className="text-center text-muted-foreground py-4">Ajánlatok betöltése...</p>}
                    {!loadingIsthereanydeal && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full table-auto border-collapse">
                          <thead>
                            <tr className="bg-secondary text-foreground">
                              <th className="px-4 py-2 text-left">Bolt</th>
                              <th className="px-4 py-2 text-left">Ár</th>
                              <th className="px-4 py-2 text-center">Kedvezmény</th>
                              <th className="px-4 py-2 text-center">Műveletek</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bestOffers.map((offer: any, index: number) => {
                              const discountPercent = offer.cut ? Math.round(offer.cut) : 0;
                              const priceAmount = offer.price?.amount || 0;
                              const regularAmount = offer.regular?.amount || 0;
                              const currency = offer.price?.currency || 'USD';
                              return (
                                <tr
                                  key={offer.url || index}
                                  className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border-b border-border last:border-b-0"
                                >
                                  <td className="px-4 py-2 text-left text-sm font-semibold">
                                    {offer.shop?.name || 'Ismeretlen bolt'}
                                  </td>
                                  <td className="px-4 py-2 text-left text-sm">
                                    {priceAmount && currency ? (
                                      <>
                                        {regularAmount && regularAmount !== priceAmount && (
                                          <s className="text-muted-foreground mr-1">{regularAmount.toFixed(2)} {currency}</s>
                                        )}
                                        <span className="font-bold text-primary">{priceAmount.toFixed(2)} {currency}</span>
                                      </>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm">
                                    {discountPercent > 0 ? (
                                      <span className="text-red-400 font-semibold">-{discountPercent}%</span>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <Button
                                      onClick={() => window.open(offer.url, '_blank')}
                                      className="text-xs bg-primary hover:bg-primary/80"
                                      size="sm"
                                    >
                                      Megnyitás
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

          </div>

          <div className="lg:col-span-1 flex flex-col gap-6">
            <GameLinksBox game={gameData} />
            <GameStatsBox game={gameData} />

            <Card className="bg-card text-card-foreground border-border shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                  <Gamepad2 size={20} /> Műfajok és Tagek
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2 justify-center">
                  {gameData.genres?.map((genre) => (
                    <Link key={`genre-${genre.id}`} to={`/?genre=${encodeURIComponent(genre.id.toString())}`}>
                      <Badge className="px-3 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border hover:bg-secondary/80 cursor-pointer">{genre.name}</Badge>
                    </Link>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {gameData.tags?.map((tag) => (
                    <Link key={`tag-${tag.id}`} to={`/?tag=${encodeURIComponent(tag.id.toString())}`}>
                      <Badge className="px-3 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground border border-primary/50 hover:bg-primary/80 cursor-pointer">{tag.name_hu || tag.name_en}</Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Új Info Panel - Adatlap metaadatai - Csak admin számára */}
            {isAdmin && (
              <Card className="bg-card text-card-foreground border-border shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-primary">📋 Adatlap Információ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Létrehozás és Szerkesztés */}
                  {gameData.created_at && (
                    <div className="flex items-start gap-3">
                      <Clock size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Létrehozva</p>
                        <p className="text-sm font-medium text-foreground">
                          {new Date(gameData.created_at).toLocaleDateString('hu-HU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  {gameData.updated_at && (
                    <div className="flex items-start gap-3">
                      <Clock size={18} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Szerkesztve</p>
                        <p className="text-sm font-medium text-foreground">
                          {new Date(gameData.updated_at).toLocaleDateString('hu-HU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Készítette */}
                  {gameData.created_by && (
                    <div className="flex items-start gap-3">
                      <User size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Készítette</p>
                        <p className="text-sm font-medium text-foreground">{gameData.created_by}</p>
                      </div>
                    </div>
                  )}

                  {/* Állapot */}
                  <div className="flex items-start gap-3 pt-2 border-t border-border">
                    <div className="flex-1 flex items-center gap-2">
                      {gameData.hidden ? (
                        <>
                          <EyeOff size={18} className="text-red-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">Állapota</p>
                            <p className="text-sm font-medium text-red-400">Rejtett</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Eye size={18} className="text-green-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">Állapota</p>
                            <p className="text-sm font-medium text-green-400">Látható</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {(isTranslator || allVideos.length > 0) && gameData.id && (
              <>
                <GameYoutubeBox videos={videosToShow} id={gameData.id} />
              </>
            )}

            {(isAdmin || isTranslator) && gameData && (
              <Card className="bg-card text-card-foreground border-border shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer" onClick={() => setIsTagsPanelOpen(p => !p)}>
                  <CardTitle className="text-xl font-bold text-primary">Admin: Tagek kezelése</CardTitle>
                  <ChevronDown className={`w-6 h-6 text-primary transition-transform ${isTagsPanelOpen ? 'rotate-180' : ''}`} />
                </CardHeader>
                {isTagsPanelOpen && (
                  <CardContent className="pt-4">
                    <div className="w-full">
                      <GameAdminTagsPanel
                        gameId={gameData.id}
                        initialTags={(gameData.tags || []).map(tag => ({ ...tag, adult: false }))} />
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
            {isLoggedIn && ( // Show this box to all logged-in users
              <GameReportAndRequestBox
                gameId={gameData.id}
                gameSlug={gameData.slug || ''}
                onSuggestVideoClick={() => setIsSuggestVideoModalOpen(true)}
              />
            )}

          </div>
        </div>



        <div className="mt-8">
          {gameData.hungarianRelease && !showMagyaritasWarningAcknowledged ? (
            <Card className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 rounded-lg p-6 my-6 text-center shadow-lg">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-amber-400 mr-3" />
                <div>
                  <h2 className="text-2xl font-bold text-amber-400">🇭🇺 A játék magyarul jelent meg!</h2>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-4">
                <Button onClick={() => setShowMagyaritasWarningAcknowledged(true)} className="bg-transparent text-amber-300 hover:text-amber-100 text-sm underline max-w-full whitespace-normal text-center">
                  Megértettem, de én még a letöltéseket szeretném megnézni
                </Button>
              </div>
            </Card>
          ) : gameData.hasOfficialTranslation && !showMagyaritasWarningAcknowledged ? (
            <Card className="bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 rounded-lg p-6 my-6 text-center shadow-lg">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-400 mr-3" />
                <div>
                  <h2 className="text-2xl font-bold text-green-400">Kész magyaritasok.hu fordítás elérhető</h2>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-4">
                <Button asChild className="bg-green-600 text-white hover:bg-green-700 text-lg px-8 py-3 rounded-full">
                  <a href={gameData.link_hun} target="_blank" rel="noopener noreferrer">Ugrás a magyaritasok.hu oldalra</a>
                </Button>
                <Button onClick={() => setShowMagyaritasWarningAcknowledged(true)} className="bg-transparent text-green-300 hover:text-green-100 text-sm underline max-w-full whitespace-normal text-center">
                  Megértettem, de én mégis innen töltenék le
                </Button>
              </div>
            </Card>
          ) : (
            // GameTabs propok frissítve a dinamikus vezérléshez
            <GameTabs
              game={gameData}
              downloadsCount={gameData.downloads}
              commentsCount={gameData.comments}
              thanksCount={gameData.likes}
              uploads={gameData.uploads ?? 0}
              activeTab={activeTab}
              onValueChange={setActiveTab}
              targetCommentPage={targetCommentPage}
              targetCommentId={targetCommentId}
              allow_comments={gameData.allow_comments}
              ads={gameData.ads}
              reason={gameData.reason}
            />
          )}


        </div>
      </main>

      {/* SEO blokk: látható, a régi reklámhely helyére illeszkedve */}
      {gameData && (
        <div className="container mx-auto p-4">
          <Card className="bg-card text-card-foreground border-border shadow-sm my-6">
            <CardContent>
              <h3 className="text-lg font-semibold text-primary mb-2">{gameData.name} Magyarítás</h3>
              <p className="text-sm text-muted-foreground">
                A {gameData.name} magyarítás letöltése itt érhető el: részletes telepítési útmutatóval és kompatibilitási információkkal a GEP.MONSTER oldalán. A {gameData.name} magyar fordítása segít, hogy a történet, párbeszédek és menük érthetőbbé váljanak a magyar játékosok számára. A fordítás egy gépi fordítás — mindig ellenőrizd a kompatibilis játékverziót a letöltés előtt. Itt megtalálod a letöltési linkeket, telepítési tippeket és a fordítással kapcsolatos fontos tudnivalókat.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
      {gameData && (
        <SuggestVideoModal
          isOpen={isSuggestVideoModalOpen}
          onClose={() => setIsSuggestVideoModalOpen(false)}
          gameId={gameData.id} />
      )}
    </div>
  );
};

const LoadingState = () => (
  <div className="min-h-screen flex flex-col">
    <div className="sticky top-0 z-50 w-full">
      <Header />
    </div>
    <main className="flex-grow container mx-auto p-4 py-8 flex items-center justify-center mt-[12px]">
      <p className="text-primary text-xl">Játék betöltése...</p>
    </main>
    <Footer />
  </div>
);

const ErrorState = ({ message, isAdmin }: { message: string | null, isAdmin: boolean }) => (
  <div className="min-h-screen flex flex-col">
    <div className="sticky top-0 z-50 w-full">
      <Header />
    </div>
    <main className="flex-grow container mx-auto p-4 py-8 text-center text-muted-foreground main-content-bg mt-[12px] flex flex-col items-center justify-center">
      <div className="mb-8 w-full">

      </div>

      <div className="bg-card rounded-lg p-8 shadow-lg mb-8">
        <h1 className="text-3xl font-bold text-primary mb-4">Hiba</h1>
        <p className="text-xl text-gray-600 mb-6">{message || "Ismeretlen hiba történt."}</p>
        <Link to="/" className="text-blue-500 hover:text-blue-700 underline text-lg">Vissza a főoldalra</Link>
      </div>

      <div className="w-full mt-8">

      </div>
    </main>
    <Footer />
  </div>
);

const GameTitleSection = ({ game, isAdmin }: { game: GameData; isAdmin: boolean }) => (
  <div className="mb-8">
    <div className="flex flex-wrap items-center gap-3 mb-2">
      <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">{game.name} {isAdmin && `#(${game.id})`}</h1>
      {game.hungarianRelease && (
        <Badge className="px-3 py-1 rounded-full text-xs font-medium bg-amber-600 text-white border border-amber-700">
          🇭🇺 Magyarul jelent meg
        </Badge>
      )}
    </div>
    <h2 className="text-2xl font-semibold text-gray-200 mb-3">{game.name} Magyarítás</h2>
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-300">
      <div className="flex items-center space-x-1"><Download className="w-4 h-4 text-blue-400" /><span>{game.downloads.toLocaleString()} letöltés</span></div>
      <div className="flex items-center space-x-1"><Heart className="w-4 h-4 text-red-400" /><span>{game.likes.toLocaleString()} lájk</span></div>
      <div className="flex items-center space-x-1"><Upload className="w-4 h-4 text-green-400" /><span>{game.uploads?.toLocaleString() || 0} fordítás</span></div>
      <div className="flex items-center space-x-1 text-purple-400"><MessageSquare className="w-4 h-4" /><span>{game.comments.toLocaleString()}</span></div>
    </div>
  </div>
);

export default GameShow;
